/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { applyTransforms } from 'components/startingPoints/relationshipGraph/RelationshipUtil.js';
import DataBuilder from 'components/startingPoints/relationshipGraph/DataBuilder';
import Config from 'components/startingPoints/relationshipGraph/Config';
export const ABANDONED_RELATIONSHIP_REQUEST = 'ABANDONED_RELATIONSHIP_REQUEST';

/**
 * This is the base request handler for relationship request main flow
 */
class RelationshipBaseRequestHandler {

	/**
	 * @private
	 * @param {JSONObject} querySpec Query specifications
	 * @param {String} requestOptions the request options
	 * @param {context} context the context info of the requset, such as the associated module API
	 * @param {service} passed in service instance
	 */
	static _sendRequest(querySpec, requestOptions, context, service) {
		if(querySpec && context && context.moduleAPI){
			return this._addQueryParams(querySpec, context)
				.then(() => {
					const targetModel = context.targetModel;
					const abandonedRequest = Object.keys(context.transformOptions)[0];
					const abandonPromise = new Promise( (resolve, reject) => {
						targetModel && targetModel.setAbandon(reject);
					}).catch(()=> Promise.reject({
						abandonedRequest,
						code: ABANDONED_RELATIONSHIP_REQUEST
					}));

					return Promise.race(
						[
							context.moduleAPI.querySmartsExploreRelationshipData({
								data : querySpec,
								requestOptions
							}),
							abandonPromise
						]
					).then( // better to use finally here, but we do not support finally in unit test
						(ajaxResponse) => {
							// promise is determined by respose or abandoned, set abandon to null
							targetModel && targetModel.setAbandon(null);
							return ajaxResponse;
						},
						(error) => {
							// promise is determined by respose or abandoned, set abandon to null
							targetModel && targetModel.setAbandon(null);
							throw error;
						}
					).catch( (error)=>{
						// if abandoned Request, directly return
						if(error && error.code === ABANDONED_RELATIONSHIP_REQUEST) {
							return Promise.reject(error);
						}
						return this._handleError(querySpec, error, context, service);
					}).then(
						(ajaxResponse)=>{
							return this._handleResponse(querySpec, ajaxResponse, context, service);
						}
					);
				});
		}
		return Promise.reject(new Error('INVALID_REQUEST_EXPLORE_sendRequest'));
	}

	static _handleError(querySpec, error, context){
		//Verify and generate a possible fallback response of 'completed' status for cetrain Error codes
		const errContext = {
			targetID: querySpec.targetFieldIDForExpression,
			glassContext: context.glassContext,
			relationshipModel: context.model
		};
		return DataBuilder.convertErrorsToCompletedResponse(error, errContext);
	}

	static _handleResponse(querySpec, ajaxResponse, context, service){
		let ajaxResponseData = ajaxResponse.data;
		if (service._isSucessfulResponse(ajaxResponseData)) {
			service._handlePredictStatus(ajaxResponseData);
			// TODO: verify if this function is really needed
			this._callback(ajaxResponseData, context);
		} else {
			//TODO: Should we reject a promise for this ASYNC function
			throw service._createError({
				smartsCode:  ajaxResponseData.status.statusCode,
				responseMessage: ajaxResponseData.status.message
			});
		}
		service._addRawDataToCache(querySpec, ajaxResponseData, context);
		if (context && context.model.getIsFilterStrengthFeatureEnabled()) {
			context.rawData = ajaxResponseData;
			// R3 behavior: filter data based on the needed transformation
			ajaxResponseData = applyTransforms(context);
		}
		return Promise.resolve(ajaxResponseData);
	}

	/**
	 * To be Overriden by subclass
	 */
	static _callback(ajaxResponseData, context){
		void([ajaxResponseData, context]);
	}

	/**
	 * This method add common parameters for relationship strength request.
	 *  Subclass should call this method before adding their specific parameters.
	 */
	static _addQueryParams(querySpec, context){
		if (context.model && context.model.isTimeoutFeatureEnabled && context.model.isTimeoutFeatureEnabled()) {
			querySpec.timeout = Config.max_request_waiting_time;
		}
		return context.moduleAPI.saveTemporaryModule()
			.then((shapingModuleId) => {
				if (context.moduleAPI.getAssetId() !== shapingModuleId) {
					querySpec.tempModuleId = shapingModuleId;
				}
			});
	}
}
export default RelationshipBaseRequestHandler;



// WEBPACK FOOTER //
// ./src/js/util/RelationshipBaseRequestHandler.js